/**
 * Constructs an Riot-compliant storage identifier for analytics-related data.
 * @param {string} name A label describing the data.
 * @returns {string}
 */
function buildStorageKey (name) {
  return `_rg_ana_app_${name}`
}

function generateDeviceId () {
  // usually randomUUID() is only available in secure contexts (HTTPS)
  if (window.location.protocol === 'https:') {
    return window.crypto.randomUUID()
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : r & 0x3 | 0x8

    return v.toString(16)
  })
}

/**
 * @typedef {Object} GeolocationInfo
 * @property {string} area The geographic region based on the session's detected location.
 * @property {string} code The {@link https://www.iso.org/obp/ui/#search ISO 3166-1 alpha-2} country based on the session's detected location.
 * @property {string} locale The {@link https://www.ietf.org/rfc/bcp/bcp47.txt RFC 5646} locale based on the session's detected location.
 */

/**
 * Gets the unique identifier for the device.
 * @returns {string}
 */
export function getDeviceId () {
  const key = buildStorageKey('device_id')

  const existingDeviceId = getLocalStorageValue(key)

  if (existingDeviceId) {
    return existingDeviceId
  }

  const deviceId = generateDeviceId()

  setLocalStorageValue(key, deviceId)

  return deviceId
}

/**
 * Returns the {@link https://www.iso.org/obp/ui/#search ISO 3166-1 alpha-2} country based on the session's detected location.
 * @returns {string | undefined}
 */
export function getGeoCountryCode () {
  const geoInfo = window._geoinfo

  if (!isGeolocationInfo(geoInfo)) {
    return undefined
  }

  return geoInfo.code
}

/**
 * Retrieves the UTM Parameters, if any, that were previously stored for the current session.
 * @returns {Partial<import('./types').UtmParameters>}
 */
export function getSessionUtmParameters () {
  const key = buildStorageKey('utm')

  return getSessionStorageValue(key)
}

/**
 * Retrieves the data matching the specified identifier, if any, from the device's local storage.
 * @template TValue The type of value.
 * @param {string} key The identifier of the data to be retrieved.
 * @returns {TValue | undefined}
 */
function getLocalStorageValue (key) {
  try {
    const value = window.localStorage.getItem(key)

    if (value === undefined || value === null) {
      return undefined
    }

    return JSON.parse(value)
  }
  catch (_) {
    return undefined
  }
}

/**
 * Retrieves the data matching the specified identifier, if any, from the device's session storage.
 * @template TValue
 * @param {string} key The identifier of the data to be retrieved.
 * @returns {TValue | undefined}
 */
function getSessionStorageValue (key) {
  try {
    const value = window.sessionStorage.getItem(key)

    if (value === undefined || value === null) {
      return undefined
    }

    return JSON.parse(value)
  }
  catch (_) {
    return undefined
  }
}

/**
 * @param {unknown} value
 * @returns {value is GeolocationInfo}
 */
function isGeolocationInfo (value) {
  if (typeof value !== 'object' || !value) {
    return false
  }

  if (!('area' in value) || typeof value.area !== 'string') {
    return false
  }

  if (!('code' in value) || typeof value.code !== 'string') {
    return false
  }

  if (!('locale' in value) || typeof value.locale !== 'string') {
    return false
  }

  return true
}

/**
 * Stores the specified data for later access in the device's local storage.
 * @template TValue
 * @param {string} key The identifier of the data to be set.
 * @param {TValue} value The data to store.
 */
function setLocalStorageValue (key, value) {
  try {
    window.localStorage.setItem(key, value)
  }
  catch (_) {
    return
  }
}

/**
 * Stores the specified data for later access in the device's session storage.
 * @template TValue
 * @param {string} key The identifier of the data to be set.
 * @param {TValue} value The data to store.
 */
function setSessionStorageValue (key, value) {
  try {
    window.sessionStorage.setItem(key, value)
  }
  catch (_) {
    return
  }
}

/**
 * Returns the provided URL without UTM Parameters and stores them, if any, for the duration of
 * the current session.
 * @param url The URL to parse and sanitize.
 */
export function storeAndTrimUtmParameters (url) {
  if (!URL.canParse(url)) {
    return url
  }

  const rawUrl = new URL(url)

  /** @type {Partial<import('./types').UtmParameters>} */
  const utmParameters = {}

  const campaign = rawUrl.searchParams.get('utm_campaign')

  if (campaign) {
    utmParameters.campaign = campaign
    rawUrl.searchParams.delete('utm_campaign')
  }

  const content = rawUrl.searchParams.get('utm_content')

  if (content) {
    utmParameters.content = content
    rawUrl.searchParams.delete('utm_content')
  }

  const medium = rawUrl.searchParams.get('utm_medium')

  if (medium) {
    utmParameters.medium = medium
    rawUrl.searchParams.delete('utm_medium')
  }

  const source = rawUrl.searchParams.get('utm_source')

  if (source) {
    utmParameters.source = source
    rawUrl.searchParams.delete('utm_source')
  }

  const term = rawUrl.searchParams.get('utm_term')

  if (term) {
    utmParameters.term = term
    rawUrl.searchParams.delete('utm_term')
  }

  if (!Object.keys(utmParameters).length) {
    return rawUrl.href
  }

  const key = buildStorageKey('utm')

  setSessionStorageValue(key, utmParameters)

  return rawUrl.href
}
